// packages/core/src/learning/schools/human-capital.ts
// ─────────────────────────────────────────────────────────────────────────
// Authored exhibit for "Human capital". Matches boglehead-indexing.ts gold
// standard.
//
// ID-bound fields (the validator + compiler guard these):
//   - figures:        FigureId[]  — empty in source AND kept []. The worldview
//                     names no figures; canon authors (Gary Becker, Adam Smith,
//                     Cal Newport, Reid Hoffman, Ben Casnocha) are NOT
//                     figures-tally entries per the protocol. So this school
//                     contributes nothing to the running figure tally.
//   - applyActions:   ApplyAction union — type-checked at compile time. Only
//                     live/allowed action types emitted.
//   - featuredLessonIds / debateIds — [] until the cross-ref pass.
// ─────────────────────────────────────────────────────────────────────────

import type { SchoolExhibitEntry } from '../SchoolExhibitMeta';

export const HUMAN_CAPITAL_EXHIBIT: SchoolExhibitEntry = {
	id: 'human-capital',
	tagline:
		'Human capital believes your earning power starts with the person carrying it.',
	worldviewSummary:
		'Human capital is paying attention to the asset you carry everywhere: your skills, health, energy, judgment, habits, and reputation. This lens protects you from thinking money growth only happens inside accounts. It also protects you from ignoring the body, mind, and skill base that make income possible. The tradeoff is that this lens can become hustle culture if it treats every hour, friendship, and hobby like an investment. The blind spot is forgetting that people are not machines, and not every barrier can be solved by working harder.',
	coreBeliefs: [
		'Human capital believes your future income is shaped by skills, energy, health, and judgment.',
		'Human capital believes learning can be one of the highest impact uses of time.',
		'Human capital believes your reputation compounds because people remember who solves problems well.',
		'Human capital believes burnout is not ambition. It is asset damage.',
		'Human capital believes career growth is not just credentials. It is useful ability under real pressure.',
	],
	warnsAgainst: [
		'Treating investing as the only wealth lever while your earning power stays weak.',
		'Confusing being busy with becoming more valuable.',
		'Ignoring health, sleep, focus, and emotional stability until they become expensive problems.',
		'Turning self improvement into endless productivity guilt.',
	],
	helps: 'This lens helps people who are early in their career, rebuilding, changing fields, or trying to raise their earning power without gambling on shortcuts. It may not fit when someone needs rest, stability, caregiving room, or protection from burnout more than another ambitious push.',
	figures: [],
	canon: [
		{
			title: 'Human Capital',
			author: 'Gary Becker',
			oneLineIdea:
				'Skills, education, health, and experience can raise a person’s productive capacity over time.',
		},
		{
			title: 'The Wealth of Nations',
			author: 'Adam Smith',
			oneLineIdea:
				'Specialized skill and useful work can change what a person can produce and earn.',
		},
		{
			title: 'So Good They Can’t Ignore You',
			author: 'Cal Newport',
			oneLineIdea:
				'Rare and valuable skills can create more career control than chasing passion alone.',
		},
		{
			title: 'Deep Work',
			author: 'Cal Newport',
			oneLineIdea:
				'Focused ability is a real edge in a distracted economy.',
		},
		{
			title: 'The Startup of You',
			author: 'Reid Hoffman and Ben Casnocha',
			oneLineIdea:
				'Your career can be managed like an adaptive asset, with learning, networks, and optionality.',
		},
	],
	featuredLessonIds: [],
	nativeModuleId: 'school-human-capital',
	debateIds: [],
	applyActions: [
		{
			label: 'See whether your current money picture gives you room to grow',
			action: {
				type: 'openDashboard',
				label: 'Check your current picture',
			},
		},
		{
			label: 'Test how higher income changes your freedom path',
			action: {
				type: 'openSimulator',
				label: 'Simulate higher income',
				scenarioType: 'side-hustle',
			},
		},
		{
			label: 'Check how much runway gives you room to learn',
			action: {
				type: 'calculateRunway',
				label: 'Check your runway',
			},
		},
		{
			label: 'Name the skill that could raise your options',
			action: {
				type: 'noToolAction',
				label: 'Pick a skill bet',
				prompt: 'Write one skill that could raise your options, why it matters, and what small proof you could build this month.',
			},
		},
	],
	marketingHooks: [
		{
			line: 'Your biggest asset might not be your portfolio yet. It might be your capacity.',
			kind: 'hook',
		},
		{
			line: 'Human capital asks a brutal question: are you building the person who earns the future?',
			kind: 'hook',
		},
		{
			line: 'What skill changed your income more than any investment you made?',
			kind: 'interview-question',
		},
		{
			line: 'Where did hustle culture get human capital wrong in your life?',
			kind: 'interview-question',
		},
		{
			line: 'Nisk helps people test money ideas, but human capital starts with the person making the decisions.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Before you chase advanced finance, understand the asset that pays your bills: you.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Ask someone what raised their income, then ask what it cost their health.',
			kind: 'creator-prompt',
		},
		{
			line: 'Show the difference between building skills and just staying busy.',
			kind: 'creator-prompt',
		},
	],
	revision: 1,
};
